import { Component, OnInit } from '@angular/core';
import { SignupStepperService } from '@app/modules/signup-new/services/signup-stepper.service';
import { SignupUserService } from '@app/modules/signup-new/services/signup-user.service';
import { SignupWizardService } from '@app/modules/signup-new/services/signup-wizard.service';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { sbnbIcon } from '@app/ui/icons';
import { finalize } from 'rxjs/operators';
import { fadeInUpAnimation } from 'src/animations';

type OnboardingFeature =
  | 'calendar_sync'
  | 'scheduled_messaging'
  | 'direct_booking_site'
  | 'smart_device'
  | 'owner_statements'
  | 'upsells'
  | 'ai_messaging'
  | 'rental_agreements';

interface Feature {
  key: OnboardingFeature;
  name: string;
  description: string;
  icon: sbnbIcon;
  selected: boolean;
}

@Component({
  selector: 'sbnb-signup-onboarding-page',
  templateUrl: './signup-onboarding-page.component.html',
  animations: [fadeInUpAnimation],
})
export class SignupOnboardingPageComponent implements OnInit {
  loading = false;
  features: Feature[] = [
    {
      key: 'calendar_sync',
      name: 'Calendar sync',
      description: 'Connect all channels to <br/> avoid double bookings',
      icon: 'calendar',
      selected: false,
    },
    {
      key: 'scheduled_messaging',
      name: 'Scheduled messaging',
      description: 'Save time with messaging & <br/> review rules',
      icon: 'message-simple',
      selected: false,
    },
    {
      key: 'direct_booking_site',
      name: 'Direct bookings',
      description: 'Set up your own booking <br/> website in minutes',
      icon: 'direct-simple',
      selected: false,
    },
    {
      key: 'smart_device',
      name: 'Smart devices',
      description: 'Automate smart locks and <br/> thermostats',
      icon: 'devices',
      selected: false,
    },
    {
      key: 'owner_statements',
      name: 'Owner statements',
      description: 'Generate statements for <br/> property owners',
      icon: 'preferences',
      selected: false,
    },
    {
      key: 'upsells',
      name: 'Upsells',
      description: 'Offer add-ons like early <br/> check-in to boost revenue',
      icon: 'sync',
      selected: false,
    },
    {
      key: 'ai_messaging',
      name: 'AI messaging',
      description: 'Handle guest questions <br/> automatically with auto-replies',
      icon: 'light-bulb-shine',
      selected: false,
    },
    {
      key: 'rental_agreements',
      name: 'Rental agreements',
      description: 'Have guests agree to custom <br/> terms before check-in',
      icon: 'properties-simple',
      selected: false,
    },
  ];
  hasSelectedFeatures = false;
  isMobile$ = this.deviceDetectionService.isMobileViewport();

  constructor(
    private signupWizardService: SignupWizardService,
    private signupStepperService: SignupStepperService,
    private signupUserService: SignupUserService,
    private deviceDetectionService: DeviceDetectionService
  ) {}

  ngOnInit() {
    this.features.forEach(
      (feature) => (feature.selected = this.signupUserService.user$.value?.onboarding?.includes(feature.key))
    );
    this.hasSelectedFeatures = this.features.some((f) => f.selected);
  }

  onContinue() {
    this.loading = true;
    this.signupUserService
      .updateOnboardingFeatures(this.features.filter((f) => f.selected).map((f) => f.key))
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(() => {
        this.signupWizardService.next('onboarding');
        this.signupStepperService.complete('Completion');
      });
  }

  toggleFeature(feature: Feature) {
    const selectedFeature = this.features.find((f) => f.name === feature.name);
    if (selectedFeature) selectedFeature.selected = !selectedFeature.selected;
    this.hasSelectedFeatures = this.features.some((f) => f.selected);
  }
}
